import { useState } from 'react';
import { ArrowRight, MailCheck, ShieldCheck } from 'lucide-react';
import { ActionStack, Screen } from '@/app/layout/Screen';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Form';
import { Helper, Kicker, Lead, MiniIcon, PageTitle, Panel, Row } from '@/components/ui/Surface';
import { useRegistrationStore } from '@/store/registrationStore';

/**
 * Подтверждение email при регистрации.
 *
 * Два подшага: ввод почты (`request_code`) и ввод кода (`confirm_code`).
 * Ошибки поля приходят из стора по машиночитаемому типу бэкенда, поэтому
 * пользователь видит конкретную причину, а не общее «что-то пошло не так».
 */
function isEmailValid(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function EmailCodeScreen() {
  // Точечные подписки вместо всего стора: иначе экран перерисовывается на
  // каждое нажатие клавиши и ввод заметно подтормаживает
  const emailStage = useRegistrationStore((state) => state.emailStage);
  const storedEmail = useRegistrationStore((state) => state.email);
  const fieldError = useRegistrationStore((state) => state.fieldError);
  const submitting = useRegistrationStore((state) => state.submitting);
  const canResend = useRegistrationStore((state) => state.canResend);
  const setEmail = useRegistrationStore((state) => state.setEmail);
  const requestCode = useRegistrationStore((state) => state.requestCode);
  const confirmCode = useRegistrationStore((state) => state.confirmCode);
  const resetFieldError = useRegistrationStore((state) => state.resetFieldError);

  // Введённое живёт в поле, а не в сторе: пока пользователь печатает, значение
  // не должно зависеть от перерисовок, продления сессии и ответов сервера
  const [emailInput, setEmailInput] = useState(storedEmail);
  const [code, setCode] = useState('');

  const submitEmail = () => {
    setEmail(emailInput);
    void requestCode();
  };

  if (emailStage === 'enter_code') {
    return (
      <Screen subtitle="Регистрация" onboarding>
        <MiniIcon tone="lime" size={48}>
          <MailCheck className="h-5 w-5" aria-hidden />
        </MiniIcon>
        <PageTitle className="mt-3.5">Введите код из письма</PageTitle>
        <Lead>
          Мы отправили код на <strong className="text-ink">{storedEmail}</strong>. Проверьте почту, включая папку
          «Спам».
        </Lead>

        <Input
          label="Код подтверждения"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="Например: 123456"
          value={code}
          invalid={Boolean(fieldError)}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
        />
        {fieldError ? <Helper error>{fieldError}</Helper> : null}

        <ActionStack>
          <Button
            variant="primary"
            block
            loading={submitting}
            disabled={code.length < 4}
            onClick={() => void confirmCode(code)}
            iconRight={<ArrowRight className="h-4 w-4" aria-hidden />}
          >
            Подтвердить
          </Button>
          {canResend ? (
            <Button variant="ghost" block disabled={submitting} onClick={() => void requestCode()}>
              Отправить код повторно
            </Button>
          ) : null}
        </ActionStack>
      </Screen>
    );
  }

  return (
    <Screen subtitle="Регистрация" onboarding>
      <Kicker>Подтверждение почты</Kicker>
      <PageTitle>Введите вашу электронную почту</PageTitle>
      <Lead>На неё придёт код подтверждения. Используйте адрес, на который оформлен доступ к курсу.</Lead>

      {/*
        Автозаполнение выключено намеренно: в десктопном клиенте оно возвращало
        стёртое значение обратно в поле и мешало исправить адрес. Подставлять
        почту тут нечего — вход подтверждается кодом из письма.
      */}
      <Input
        label="Электронная почта"
        type="email"
        inputMode="email"
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        data-lpignore="true"
        data-form-type="other"
        placeholder="name@example.com"
        value={emailInput}
        invalid={Boolean(fieldError)}
        onChange={(event) => {
          setEmailInput(event.target.value);
          if (fieldError) resetFieldError();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && isEmailValid(emailInput)) submitEmail();
        }}
      />
      {fieldError ? <Helper error>{fieldError}</Helper> : null}

      <Panel tone="tint" className="mt-3.5">
        <Row
          icon={
            <MiniIcon tone="lime">
              <ShieldCheck className="h-[17px] w-[17px]" aria-hidden />
            </MiniIcon>
          }
          title="Без пароля"
          subtitle="Вход подтверждается кодом из письма и дальше выполняется автоматически через мессенджер."
        />
      </Panel>

      <ActionStack>
        <Button
          variant="primary"
          block
          loading={submitting}
          disabled={!isEmailValid(emailInput)}
          onClick={submitEmail}
          iconRight={<ArrowRight className="h-4 w-4" aria-hidden />}
        >
          Получить код
        </Button>
      </ActionStack>
    </Screen>
  );
}
